import React, { useEffect, useState } from 'react';
import { auditApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, RefreshCw, ShieldAlert, LogIn, LogOut as LogOutIcon, UserPlus, UserCog, KeyRound } from 'lucide-react';

const ACCION_META = {
  LOGIN_OK:           { label: 'Inicio de sesión', icon: LogIn,       tone: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  LOGIN_FAILED:       { label: 'Login fallido',    icon: ShieldAlert, tone: 'bg-red-50 border-red-200 text-red-700' },
  LOGOUT:             { label: 'Cierre de sesión', icon: LogOutIcon,  tone: 'bg-ink-100 border-ink-200 text-ink-700' },
  USER_CREATE:        { label: 'Usuario creado',   icon: UserPlus,    tone: 'bg-brand-50 border-brand-200 text-brand-700' },
  USER_UPDATE:        { label: 'Usuario editado',  icon: UserCog,     tone: 'bg-blue-50 border-blue-200 text-blue-700' },
  USER_DEACTIVATE:    { label: 'Usuario desactivado', icon: UserCog,  tone: 'bg-ink-100 border-ink-200 text-ink-700' },
  USER_PASSWORD_RESET:{ label: 'Contraseña reseteada', icon: KeyRound, tone: 'bg-amber-50 border-amber-200 text-amber-700' },
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [accion, setAccion] = useState('');
  const [q, setQ] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (accion) filters.accion = accion;
      const [list, cnt] = await Promise.all([
        auditApi.list({ limit: 200, ...filters }),
        auditApi.count(filters),
      ]);
      setLogs(list);
      setTotal(cnt.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [accion]);

  const filtered = logs.filter((l) => {
    if (!q) return true;
    const qq = q.toLowerCase();
    return (
      (l.usuario_email || '').toLowerCase().includes(qq) ||
      (l.accion || '').toLowerCase().includes(qq) ||
      (l.ip || '').includes(qq) ||
      (l.path || '').toLowerCase().includes(qq)
    );
  });

  return (
    <div data-testid="audit-log-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Auditoría</h1>
          <p className="text-ink-500 mt-1">
            Registro inmutable de accesos y acciones. Total actual:{' '}
            <span className="font-semibold text-ink-800" data-testid="audit-total">{total}</span>.
          </p>
        </div>
        <Button variant="outline" onClick={cargar} data-testid="btn-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </Button>
      </div>

      <Card className="p-4 mb-6 flex flex-col md:flex-row gap-3 border-ink-200">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
          <Input
            placeholder="Filtrar por email, IP, path, acción…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white border-ink-200"
            data-testid="filter-q"
          />
        </div>
        <select
          value={accion}
          onChange={(e) => setAccion(e.target.value)}
          className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700"
          data-testid="filter-accion"
        >
          <option value="">Todas las acciones</option>
          {Object.keys(ACCION_META).map((k) => (
            <option key={k} value={k}>{ACCION_META[k].label}</option>
          ))}
        </select>
      </Card>

      <Card className="border-ink-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200 text-ink-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Cuándo</th>
                <th className="px-4 py-3 text-left font-semibold">Acción</th>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Detalle</th>
                <th className="px-4 py-3 text-left font-semibold">IP</th>
                <th className="px-4 py-3 text-right font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100" data-testid="audit-tbody">
              {loading && (
                <tr><td colSpan={6} className="text-center py-10 text-ink-400">Cargando…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-ink-400">Sin registros</td></tr>
              )}
              {!loading && filtered.map((l) => {
                const meta = ACCION_META[l.accion] || { label: l.accion, icon: ShieldAlert, tone: 'bg-ink-100 border-ink-200 text-ink-700' };
                const Icon = meta.icon;
                return (
                  <tr key={l.id} className="hover:bg-ink-50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-ink-600">
                      {fmtDate(l.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${meta.tone} border`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {meta.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {l.usuario_email ? (
                        <>
                          <p className="font-semibold text-ink-900 text-xs">{l.usuario_email}</p>
                          <p className="text-[11px] text-ink-500">{l.rol || '—'}</p>
                        </>
                      ) : (
                        <span className="text-ink-400 text-xs italic">anónimo</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <p className="text-ink-700 text-xs">
                        {l.metodo && <span className="font-mono text-ink-500">{l.metodo}</span>}{' '}
                        <span className="text-ink-700">{l.path || l.recurso}</span>
                      </p>
                      {l.detalle && Object.keys(l.detalle).length > 0 && (
                        <details className="mt-1 text-[11px] text-ink-500">
                          <summary className="cursor-pointer hover:text-ink-700">ver detalle</summary>
                          <pre className="mt-1 bg-ink-50 p-2 rounded font-mono text-[10px] overflow-x-auto">
                            {JSON.stringify(l.detalle, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-500">{l.ip || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {l.status ? (
                        <span className={`font-mono text-xs px-2 py-0.5 rounded ${
                          l.status < 300 ? 'bg-emerald-50 text-emerald-700' :
                          l.status < 400 ? 'bg-blue-50 text-blue-700' :
                          l.status < 500 ? 'bg-amber-50 text-amber-700' :
                                           'bg-red-50 text-red-700'
                        }`}>
                          {l.status}
                        </span>
                      ) : <span className="text-ink-300 text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AuditLog;
